import { Controller } from "@hotwired/stimulus"
import consumer from "../channels/consumer"

// Intervalle de mise a jour du timer (1 seconde)
const TIMER_INTERVAL_MS = 1000
// Duree d'affichage des annonces (5 secondes)
const ANNOUNCEMENT_DURATION_MS = 5000
// Panel affiche par defaut a la connexion
const DEFAULT_PANEL = "chat"

export default class extends Controller {
  static targets = ["timer", "participantCount", "panel", "tabBtn", "announcementInput", "chatBadge", "documentsBadge"]
  static values = {
    sessionId: Number,
    status: String,
    startedAt: String,
    announceUrl: String
  }

  connect() {
    this.activePanel = DEFAULT_PANEL
    this.unreadChatCount = 0
    this.unreadDocumentCount = 0
    this.subscribeToSession()

    if (this.statusValue === "live") {
      this.startTimer()
    }

    this.showPanel(DEFAULT_PANEL)
  }

  disconnect() {
    this.stopTimer()
    this.unsubscribeFromSession()
  }

  // --- ActionCable ---

  subscribeToSession() {
    this.channel = consumer.subscriptions.create(
      { channel: "LiveSessionChannel", live_session_id: this.sessionIdValue },
      {
        connected: () => {
          console.log("[LiveSession] Connected to channel")
        },

        disconnected: () => {
          console.log("[LiveSession] Disconnected from channel")
        },

        received: (data) => {
          this.handleBroadcast(data)
        }
      }
    )
  }

  unsubscribeFromSession() {
    if (this.channel) {
      this.channel.unsubscribe()
      this.channel = null
    }
  }

  // --- Broadcast handler ---

  handleBroadcast(data) {
    switch (data.type) {
      case "session_started":
        this.statusValue = "live"
        this.startedAtValue = data.started_at
        this.startTimer()
        // Signale au controller Jitsi que les élèves en attente peuvent rejoindre.
        this.dispatch("sessionStarted")
        break
      case "session_ended":
        this.statusValue = "ended"
        this.stopTimer()
        // Signale au controller Jitsi de couper proprement la visio.
        this.dispatch("sessionEnded")
        break
      case "session_restarted":
        // La session a été relancée — recharger la page pour basculer vers le layout live
        window.location.reload()
        break
      case "participant_count":
        this.updateParticipantCount(data.count)
        break
      case "announcement":
        this.showAnnouncement(data.message, data.sender)
        break
      case "poll_launched":
        this.dispatch("pollLaunched", { detail: data })
        break
      case "poll_results":
        this.dispatch("pollResults", { detail: data })
        break
      case "poll_closed":
        this.dispatch("pollClosed", { detail: data })
        break
      case "poll_deleted":
        this.dispatch("pollDeleted", { detail: data })
        break
      case "quiz_launched":
        this.dispatch("quizLaunched", { detail: data })
        break
      case "quiz_answered":
        this.dispatch("quizAnswered", { detail: data })
        break
      case "quiz_closed":
        this.dispatch("quizClosed", { detail: data })
        break
      case "quiz_deleted":
        this.dispatch("quizDeleted", { detail: data })
        break
    }
  }

  // --- Side panel switching ---

  switchPanel(event) {
    event.preventDefault()
    const panelName = event.currentTarget.dataset.panel
    this.showPanel(panelName)
  }

  showPanel(name) {
    this.activePanel = name

    this.panelTargets.forEach((panel) => {
      if (panel.dataset.panel === name) {
        panel.classList.remove("hidden")
      } else {
        panel.classList.add("hidden")
      }
    })

    // Mettre a jour le style actif des onglets
    this.tabBtnTargets.forEach((btn) => {
      const isActive = btn.dataset.panel === name

      // Trainer tabs utilisent des border-based styles
      if (btn.classList.contains("border-b-2")) {
        if (isActive) {
          btn.classList.add("text-blue-600", "border-blue-500", "bg-white")
          btn.classList.remove("text-gray-500", "border-transparent")
        } else {
          btn.classList.remove("text-blue-600", "border-blue-500", "bg-white")
          btn.classList.add("text-gray-500", "border-transparent")
        }
      } else {
        // Student tabs utilisent les classes filter-tab
        btn.classList.toggle("student-filter-tab-active", isActive)
        btn.classList.toggle("student-filter-tab-inactive", !isActive)
      }
    })

    // Reset du badge chat quand on ouvre l'onglet chat
    if (name === "chat") {
      this.unreadChatCount = 0
      this.updateChatBadge()
    }

    // Reset du badge documents quand on ouvre l'onglet documents
    if (name === "documents") {
      this.unreadDocumentCount = 0
      this.updateDocumentsBadge()
    }
  }

  // --- Chat badge ---

  onNewMessage() {
    if (this.activePanel !== "chat") {
      this.unreadChatCount++
      this.updateChatBadge()
    }
  }

  updateChatBadge() {
    if (!this.hasChatBadgeTarget) return

    if (this.unreadChatCount > 0) {
      this.chatBadgeTarget.textContent = this.unreadChatCount > 99 ? "99+" : this.unreadChatCount
      this.chatBadgeTarget.classList.remove("hidden")
      this.chatBadgeTarget.classList.add("inline-flex")
    } else {
      this.chatBadgeTarget.classList.add("hidden")
      this.chatBadgeTarget.classList.remove("inline-flex")
    }
  }

  // --- Documents badge ---

  onNewDocument() {
    if (this.activePanel !== "documents") {
      this.unreadDocumentCount++
      this.updateDocumentsBadge()
    }
  }

  updateDocumentsBadge() {
    if (!this.hasDocumentsBadgeTarget) return

    if (this.unreadDocumentCount > 0) {
      this.documentsBadgeTarget.textContent = this.unreadDocumentCount > 99 ? "99+" : this.unreadDocumentCount
      this.documentsBadgeTarget.classList.remove("hidden")
      this.documentsBadgeTarget.classList.add("inline-flex")
    } else {
      this.documentsBadgeTarget.classList.add("hidden")
      this.documentsBadgeTarget.classList.remove("inline-flex")
    }
  }

  // --- Timer ---

  startTimer() {
    this.stopTimer()
    this.timerInterval = setInterval(() => {
      this.updateTimerDisplay()
    }, TIMER_INTERVAL_MS)
    // Afficher immediatement sans attendre le premier tick
    this.updateTimerDisplay()
  }

  stopTimer() {
    if (this.timerInterval) {
      clearInterval(this.timerInterval)
      this.timerInterval = null
    }
  }

  updateTimerDisplay() {
    if (!this.hasTimerTarget || !this.startedAtValue) return

    const startedAt = new Date(this.startedAtValue)
    const now = new Date()
    const elapsedSeconds = Math.floor((now - startedAt) / 1000)

    const hours = Math.floor(elapsedSeconds / 3600)
    const minutes = Math.floor((elapsedSeconds % 3600) / 60)
    const seconds = elapsedSeconds % 60

    const pad = (n) => String(n).padStart(2, "0")
    this.timerTarget.textContent = `${pad(hours)}:${pad(minutes)}:${pad(seconds)}`
  }

  // --- Participant count ---

  updateParticipantCount(count) {
    if (this.hasParticipantCountTarget) {
      this.participantCountTarget.textContent = count
    }
  }

  // --- Announcements ---

  async sendAnnouncement(event) {
    event.preventDefault()

    if (!this.hasAnnouncementInputTarget || !this.announceUrlValue) return
    const message = this.announcementInputTarget.value.trim()
    if (!message) return

    this.announcementInputTarget.value = ""

    const token = document.querySelector('meta[name="csrf-token"]')?.content
    try {
      await fetch(this.announceUrlValue, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-CSRF-Token": token,
          "Accept": "application/json"
        },
        body: JSON.stringify({ message })
      })
    } catch (error) {
      console.error("[LiveSession] Announce error:", error)
    }
  }

  showAnnouncement(message, sender) {
    const container = document.createElement("div")
    container.classList.add(
      "fixed", "top-4", "right-4", "z-50",
      "bg-blue-600", "text-white",
      "px-6", "py-4", "rounded-xl", "shadow-2xl",
      "max-w-sm", "animate-fade-in"
    )

    const senderEl = document.createElement("p")
    senderEl.classList.add("font-semibold", "text-sm", "mb-1")
    senderEl.textContent = sender || "Formateur"

    const messageEl = document.createElement("p")
    messageEl.classList.add("text-sm")
    messageEl.textContent = message

    container.appendChild(senderEl)
    container.appendChild(messageEl)
    document.body.appendChild(container)

    setTimeout(() => {
      container.remove()
    }, ANNOUNCEMENT_DURATION_MS)
  }
}
